import { useState, useRef } from "react";
import { ChevronDown, Plus, Trash2, Type, Hash, Palette, Pilcrow, ToggleLeft } from "lucide-react";
import { useClasses } from "../context/ClassesContext";
import { useTags } from "../context/TagsContext";
import { useKeyPairs } from "../context/KeyPairsContext";
import { useBg } from "../hooks/useBg";
import { useShorthands } from "../context/ShorthandContext";
import { MiniCanvas } from "./HandwritingCanvas";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";

// Section must be defined OUTSIDE RightPanel so React doesn't treat it as a
// new component on every render (which would remount children and unfocus inputs).
function Section({ id, label, count, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-b border-[#1a1a1a]">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-[#0F0F0F]"
        data-testid={`right-section-${id}`}
      >
        <span className="text-[10px] uppercase tracking-wider text-[#A1A1AA] font-medium">{label}</span>
        <div className="flex items-center gap-1.5">
          {count > 0 && <span className="text-[9px] bg-[#1a1a1a] text-[#A1A1AA] px-1.5 py-0.5 rounded-full">{count}</span>}
          <ChevronDown className={`w-3 h-3 text-[#A1A1AA] transition-transform ${open ? "" : "-rotate-90"}`} />
        </div>
      </button>
      {open && <div className="px-3 pb-3 space-y-1">{children}</div>}
    </div>
  );
}

/**
 * RightPanel — Iter40 user request:
 *   "lets move classes, tags, and key-pairs to the right menu."
 *
 * A clean right-side panel hosting Classes, Tags, and Key-Pairs with
 * minimal add/list/delete interactions. (Folders, drag-drop reordering,
 * scope grouping, and the elaborate sub-features that live in the left
 * Sidebar's versions are intentionally omitted here — we want a focused
 * meta-data panel that doesn't compete with the left nav for space.)
 *
 * Each section is a collapsible group. Add forms live inline at the top
 * of each section. Delete is a hover-button on each row.
 */

const KP_TYPE_ICON = {
  string: Pilcrow, number: Hash, color: Palette, font: Type, boolean: ToggleLeft,
};
const KP_TYPES = [
  { value: "string", label: "Text" },
  { value: "number", label: "Number" },
  { value: "color", label: "Color" },
  { value: "font", label: "Font" },
  { value: "boolean", label: "Boolean" },
];

const PRESET_COLORS = ["#0055FF", "#FF3B30", "#FFCC00", "#00C781", "#FF8A00", "#A855F7", "#EC4899", "#14B8A6"];

export default function RightPanel({ onToggleCollapse, autoCollapse = false, onToggleAutoCollapse, onAutoCollapseLeave }) {
  const { classes, create: createClass, remove: removeClass } = useClasses();
  const { tags, create: createTag, remove: removeTag } = useTags();
  const { pairs: keyPairs, create: createKp, remove: removeKp } = useKeyPairs();
  const { entries: shorthands, create: createSh, update: updateSh, remove: removeSh } = useShorthands() || {};
  const [newShTrigger, setNewShTrigger] = useState("");
  const [newShExpansion, setNewShExpansion] = useState("");
  const [newShSymbol, setNewShSymbol] = useState(null);

  const [collapsed, setCollapsed] = useState(new Set());
  const toggle = (k) => setCollapsed((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const isCollapsed = (k) => collapsed.has(k);

  // Class add form
  const [newClassName, setNewClassName] = useState("");
  const [newClassColor, setNewClassColor] = useState(PRESET_COLORS[0]);
  // Tag add form
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(PRESET_COLORS[0]);
  // KeyPair add form
  const kpKeyRef = useRef(null);
  const [newKpKey, setNewKpKey] = useState("");
  const [newKpVal, setNewKpVal] = useState("");
  const [newKpType, setNewKpType] = useState("string");

  const addClass = async () => {
    if (!newClassName.trim()) return;
    await createClass({ name: newClassName.trim(), color: newClassColor, scope: "global" });
    setNewClassName("");
  };
  const addTag = async () => {
    if (!newTagName.trim()) return;
    await createTag({ name: newTagName.trim(), color: newTagColor });
    setNewTagName("");
  };
  const addKp = async () => {
    if (!newKpKey.trim()) return;
    await createKp(newKpKey.trim(), newKpVal, newKpType);
    setNewKpKey(""); setNewKpVal("");
    setTimeout(() => { kpKeyRef.current?.focus(); }, 0);
  };

  const ColorDot = ({ value, onChange }) => (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-5 h-5 rounded border border-[#2A2A2A] flex-shrink-0"
          style={{ background: value }}
          title="Pick color"
        />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-1.5 bg-[#141414] border-[#2A2A2A]" align="start">
        <div className="grid grid-cols-4 gap-1">
          {PRESET_COLORS.map((c) => (
            <button
              key={c}
              onClick={() => onChange(c)}
              className={`w-5 h-5 rounded border ${value === c ? "ring-2 ring-[#5A90F5] border-[#5A90F5]" : "border-[#2A2A2A]"}`}
              style={{ background: c }}
            />
          ))}
        </div>
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full mt-1 h-6 bg-transparent cursor-pointer rounded"
        />
      </PopoverContent>
    </Popover>
  );

  const { rightPanelStyle } = useBg();

  return (
    <aside
      className="h-full flex flex-col bg-[#0c0c0c] border-l border-[#2A2A2A] overflow-y-auto"
      onMouseLeave={() => { if (onAutoCollapseLeave) setTimeout(onAutoCollapseLeave, 250); }}
      data-cresc-rightpanel
      data-testid="right-panel"
      data-cresc-sidebar
      style={rightPanelStyle}
    >
      <div className="px-4 py-3 border-b border-[#2A2A2A] flex items-center">
        <button
          onClick={onToggleCollapse}
          className="mr-1 flex-shrink-0 p-1 rounded hover:bg-[#1a1a1a] text-[#52525B] hover:text-[#A1A1AA] transition-colors font-mono text-[11px] font-bold"
          title="Collapse panel"
        >{">>"}</button>
        <button
          onClick={onToggleAutoCollapse}
          className={`mr-2 flex-shrink-0 p-1 rounded text-[11px] transition-colors ${autoCollapse ? "text-[#5A90F5] bg-[#5A90F5]/10" : "text-[#52525B] hover:text-[#A1A1AA]"}`}
          title={autoCollapse ? "Auto-collapse ON" : "Auto-collapse OFF"}
        >⇔</button>
        <div className="meta-label">Meta</div>
      </div>

      <Section id="classes" label="Classes" count={classes.length}>
        <div className="flex items-center gap-1.5">
          <ColorDot value={newClassColor} onChange={setNewClassColor} />
          <input
            value={newClassName}
            onChange={(e) => setNewClassName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addClass(); }}
            placeholder="New class…"
            className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-1 text-[11px] text-[#EDEDED] outline-none focus:border-[#555]"
            data-testid="right-class-new"
          />
          <button onClick={addClass} className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]" data-testid="right-class-add">
            <Plus className="w-3 h-3" />
          </button>
        </div>
        {classes.length === 0 && <div className="text-[10px] text-[#52525B] italic px-1 py-1">No classes yet</div>}
        {classes.map((c) => (
          <div key={c.class_id} className="group flex items-center gap-1.5 px-1.5 py-1 rounded hover:bg-[#151515]" data-testid={`right-class-row-${c.class_id}`}>
            <span className="w-3 h-3 rounded-full border border-[#2A2A2A]" style={{ background: c.color || "#5A90F5" }} />
            <span className="flex-1 truncate text-[11px] text-[#EDEDED]">{c.name}</span>
            <button onClick={() => removeClass(c.class_id)} className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#FF3B30]/10 text-[#FF3B30]">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </Section>

      <Section id="tags" label="Tags" count={tags.length}>
        <div className="flex items-center gap-1.5">
          <ColorDot value={newTagColor} onChange={setNewTagColor} />
          <input
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addTag(); }}
            placeholder="New tag…"
            className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-1 text-[11px] text-[#EDEDED] outline-none focus:border-[#555]"
            data-testid="right-tag-new"
          />
          <button onClick={addTag} className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]" data-testid="right-tag-add">
            <Plus className="w-3 h-3" />
          </button>
        </div>
        {tags.length === 0 && <div className="text-[10px] text-[#52525B] italic px-1 py-1">No tags yet</div>}
        {tags.map((t) => (
          <div key={t.tag_id} className="group flex items-center gap-1.5 px-1.5 py-1 rounded hover:bg-[#151515]" data-testid={`right-tag-row-${t.tag_id}`}>
            <span className="w-3 h-3 rounded-full border border-[#2A2A2A]" style={{ background: t.color || "#5A90F5" }} />
            <span className="flex-1 truncate text-[11px] text-[#EDEDED]">{t.name}</span>
            <button onClick={() => removeTag(t.tag_id)} className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#FF3B30]/10 text-[#FF3B30]">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
      </Section>

      <Section id="keypairs" label="Key Pairs" count={keyPairs.length}>
        <div className="flex items-center gap-1">
          <input
            ref={kpKeyRef}
            value={newKpKey}
            onChange={(e) => setNewKpKey(e.target.value)}
            placeholder="key"
            className="w-16 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-1 outline-none focus:border-[#555] text-[11px] font-mono"
            data-testid="right-kp-new-key"
          />
          <span className="text-[#555] text-xs">=</span>
          <div className="flex-1 min-w-0 flex items-stretch bg-[#0A0A0A] border border-[#2A2A2A] rounded focus-within:border-[#555]">
            {newKpType === "color" ? (
              <input type="color" value={newKpVal || "#5A90F5"} onChange={(e) => setNewKpVal(e.target.value)} className="flex-1 h-full bg-transparent cursor-pointer outline-none" data-testid="right-kp-new-value" />
            ) : newKpType === "boolean" ? (
              <select value={newKpVal || "true"} onChange={(e) => setNewKpVal(e.target.value)} className="flex-1 min-w-0 bg-transparent px-1.5 py-1 outline-none text-[11px]" data-testid="right-kp-new-value">
                <option value="true">true</option>
                <option value="false">false</option>
              </select>
            ) : (
              <input
                type={newKpType === "number" ? "number" : "text"}
                value={newKpVal}
                onChange={(e) => setNewKpVal(e.target.value)}
                placeholder="value"
                className="flex-1 min-w-0 bg-transparent px-1.5 py-1 outline-none text-[11px]"
                onKeyDown={(e) => { if (e.key === "Enter") addKp(); }}
                data-testid="right-kp-new-value"
              />
            )}
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className="px-1.5 border-l border-[#2A2A2A] text-[#A1A1AA] hover:text-[#EDEDED] flex items-center" data-testid="right-kp-new-type">
                  {(() => { const I = KP_TYPE_ICON[newKpType] || Type; return <I className="w-3 h-3" />; })()}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-32 p-1 bg-[#141414] border-[#2A2A2A]" align="end" sideOffset={4}>
                {KP_TYPES.map((opt) => {
                  const I = KP_TYPE_ICON[opt.value];
                  return (
                    <button key={opt.value} type="button" onClick={() => setNewKpType(opt.value)} className={`w-full flex items-center gap-1.5 px-2 py-1 rounded text-[11px] hover:bg-[#1a1a1a] ${newKpType === opt.value ? "text-[#5A90F5]" : "text-[#EDEDED]"}`}>
                      <I className="w-3 h-3" /> {opt.label}
                    </button>
                  );
                })}
              </PopoverContent>
            </Popover>
          </div>
          <button onClick={addKp} className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]" data-testid="right-kp-add">
            <Plus className="w-3 h-3" />
          </button>
        </div>
        {keyPairs.length === 0 && <div className="text-[10px] text-[#52525B] italic px-1 py-1">No key-pairs yet</div>}
        {keyPairs.map((kp) => {
          const I = KP_TYPE_ICON[kp.value_type] || Type;
          return (
            <div key={kp.kp_id} className="group flex items-center gap-1 px-1.5 py-1 rounded hover:bg-[#151515] text-[11px]" data-testid={`right-kp-row-${kp.kp_id}`}>
              <I className="w-3 h-3 text-[#52525B] flex-shrink-0" />
              <span className="font-mono text-[#EDEDED] truncate">{kp.key}</span>
              <span className="text-[#555]">=</span>
              {kp.value_type === "color" ? (
                <span className="w-3 h-3 rounded border border-[#2A2A2A] flex-shrink-0" style={{ background: kp.value }} />
              ) : (
                <span className="flex-1 truncate text-[#A1A1AA]">{String(kp.value)}</span>
              )}
              <button onClick={() => removeKp(kp.kp_id)} className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#FF3B30]/10 text-[#FF3B30] flex-shrink-0">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          );
        })}
      </Section>

      {/* ── Shorthand ── */}
      <Section id="shorthands" label="Shorthand" count={shorthands?.length || 0}>

        {/* ─ Typed shorthands ─ */}
        <div className="text-[9px] font-semibold text-[#3A3A3A] uppercase tracking-widest mb-1.5 mt-1">Typed</div>
        {(shorthands || []).filter(sh => sh.kind !== "written").map(sh => (
          <div key={sh.entry_id} className="flex items-center gap-1.5 py-0.5 group">
            <input value={sh.trigger} onChange={e => updateSh?.(sh.entry_id, { trigger: e.target.value })}
              className="w-16 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-0.5 text-[10px] font-mono outline-none focus:border-[#555] text-[#5A90F5]"
              placeholder="!trigger"/>
            <span className="text-[#3A3A3A] text-[10px]">→</span>
            <input value={sh.expansion} onChange={e => updateSh?.(sh.entry_id, { expansion: e.target.value })}
              className="flex-1 min-w-0 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-0.5 text-[10px] outline-none focus:border-[#555] text-[#EDEDED]"
              placeholder="expansion"/>
            <button onClick={() => removeSh?.(sh.entry_id)} className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#1a1a1a]">
              <Trash2 className="w-3 h-3 text-[#FF3B30]"/>
            </button>
          </div>
        ))}
        {/* New typed shorthand row */}
        <div className="flex items-center gap-1 mt-1">
          <input value={newShTrigger} onChange={e => setNewShTrigger(e.target.value)}
            placeholder="!trigger"
            className="w-16 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-1 outline-none focus:border-[#555] text-[11px] font-mono text-[#5A90F5]"/>
          <span className="text-[#3A3A3A] text-[10px]">→</span>
          <input value={newShExpansion} onChange={e => setNewShExpansion(e.target.value)}
            placeholder="expansion"
            onKeyDown={async e => {
              if (e.key === "Enter" && newShTrigger.trim() && newShExpansion.trim()) {
                await createSh?.(newShTrigger.trim(), newShExpansion.trim());
                setNewShTrigger(""); setNewShExpansion("");
              }
            }}
            className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-1 outline-none focus:border-[#555] text-[11px] text-[#EDEDED]"/>
          <button onClick={async () => {
            if (!newShTrigger.trim() || !newShExpansion.trim()) return;
            await createSh?.(newShTrigger.trim(), newShExpansion.trim());
            setNewShTrigger(""); setNewShExpansion("");
          }} className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]">
            <Plus className="w-3.5 h-3.5"/>
          </button>
        </div>

        {/* ─ Written shorthands ─ */}
        <div className="text-[9px] font-semibold text-[#3A3A3A] uppercase tracking-widest mb-1.5 mt-3">Written</div>
        <p className="text-[9px] text-[#252525] mb-2">Draw a symbol, type what it expands to. AI matches fuzzy when you write in docs.</p>
        {(shorthands || []).filter(sh => sh.kind === "written").map(sh => (
          <div key={sh.entry_id} className="flex items-center gap-2 py-1 group">
            <MiniCanvas dataUrl={sh.symbol || null}
              onChange={url => updateSh?.(sh.entry_id, { symbol: url })}
              width={52} height={36}/>
            <span className="text-[#3A3A3A] text-[10px]">→</span>
            <input value={sh.expansion} onChange={e => updateSh?.(sh.entry_id, { expansion: e.target.value })}
              className="flex-1 min-w-0 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-0.5 text-[10px] outline-none focus:border-[#555] text-[#EDEDED]"
              placeholder="expansion"/>
            <button onClick={() => removeSh?.(sh.entry_id)} className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#1a1a1a]">
              <Trash2 className="w-3 h-3 text-[#FF3B30]"/>
            </button>
          </div>
        ))}
        {/* New written shorthand row */}
        <div className="flex items-center gap-2 mt-1">
          <MiniCanvas dataUrl={newShSymbol} onChange={url => setNewShSymbol(url)} width={52} height={36}/>
          <span className="text-[#3A3A3A] text-[10px]">→</span>
          <input value={newShExpansion} onChange={e => setNewShExpansion(e.target.value)}
            placeholder="expansion"
            onKeyDown={async e => {
              if (e.key === "Enter" && newShExpansion.trim()) {
                await createSh?.(`written_${Date.now()}`, newShExpansion.trim(), { kind: "written", symbol: newShSymbol });
                setNewShExpansion(""); setNewShSymbol(null);
              }
            }}
            className="flex-1 bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1.5 py-1 outline-none focus:border-[#555] text-[11px] text-[#EDEDED]"/>
          <button onClick={async () => {
            if (!newShExpansion.trim()) return;
            await createSh?.(`written_${Date.now()}`, newShExpansion.trim(), { kind: "written", symbol: newShSymbol });
            setNewShExpansion(""); setNewShSymbol(null);
          }} className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA]">
            <Plus className="w-3.5 h-3.5"/>
          </button>
        </div>
      </Section>
    </aside>
  );
}
